import type { ComponentType } from 'preact'
import { useEffect, useState } from 'preact/hooks'

/**
 * Phase 2's optional pieces on `Session` (D-053), read by duck typing: a missing or `null` slot
 * means "show the placeholder". The hub never implements them; it only mounts or reads them.
 */
export interface StoreLike<T> {
  get(): T
  subscribe(listener: (value: T) => void): () => void
}

/** The fields of D-053's `ProfileView` the hub reads; `isGuest` is asked for in O-27. */
export interface ProfileLike {
  displayName?: string
  level?: number
  xpIntoLevel?: number
  xpForNext?: number
  coins?: number
  gems?: number
  isGuest?: boolean
  /** Counters the profile card's stats grid shows (T3.7). */
  races?: number
  wins?: number
  finishes?: number
}

export interface WalletLike {
  coins?: number
  gems?: number
}

export interface Phase2Slots {
  profile: StoreLike<ProfileLike | null> | null
  wallet: StoreLike<WalletLike | null> | null
  openProfile: (() => void) | null
  /** T2.5's task box, opened by the Tasks button. */
  DailyBox: ComponentType | null
  /** T2.6's sign-in buttons, shown on the "Sign in to unlock" overlay and the profile card. */
  SignIn: ComponentType<{ full?: boolean }> | null
  /** T2.6's name picker: `session.rename()` opens it (a method, not a component; O-39). */
  rename: (() => void) | null
  /** T2.7's party panel, shown in the Play with friends popover. */
  PartyPanel: ComponentType | null
}

type Obj = Record<string, unknown>

function isStore(v: unknown): v is StoreLike<unknown> {
  const o = v as Obj | null
  return (
    typeof o === 'object' &&
    o !== null &&
    typeof o.get === 'function' &&
    typeof o.subscribe === 'function'
  )
}

function isComponent(v: unknown): v is ComponentType {
  return typeof v === 'function'
}

/** Read the D-053 slots that exist on `session` (anything else is null). */
export function phase2Slots(session: object): Phase2Slots {
  const s = session as Obj
  const daily = s.daily as Obj | null | undefined
  const party = s.party as Obj | null | undefined
  const openProfile = s.openProfile
  const rename = s.rename
  return {
    profile: isStore(s.profile) ? (s.profile as StoreLike<ProfileLike | null>) : null,
    wallet: isStore(s.wallet) ? (s.wallet as StoreLike<WalletLike | null>) : null,
    openProfile: typeof openProfile === 'function' ? () => void openProfile.call(session) : null,
    DailyBox: isComponent(s.DailyBox)
      ? s.DailyBox
      : isComponent(daily?.DailyBox)
        ? daily.DailyBox
        : null,
    SignIn: isComponent(s.signIn) ? (s.signIn as ComponentType<{ full?: boolean }>) : null,
    rename: typeof rename === 'function' ? () => void rename.call(session) : null,
    PartyPanel: isComponent(party?.Panel) ? party.Panel : null,
  }
}

/** Current value of an optional store, re-rendering on change; null when there is no store. */
export function useMaybeStore<T>(store: StoreLike<T | null> | null): T | null {
  const [value, setValue] = useState<T | null>(() => store?.get() ?? null)
  useEffect(() => {
    if (!store) return
    setValue(() => store.get())
    return store.subscribe((v) => setValue(() => v))
  }, [store])
  return value
}

export interface TopBarValues {
  coins: number
  gems: number
  level: number
  /** XP bar fill, 0..1. */
  xp: number
}

export const count = (n: unknown): number =>
  typeof n === 'number' && Number.isFinite(n) && n > 0 ? Math.floor(n) : 0

/** Top-bar numbers: coins and gems from the wallet when present, else the profile; else 0 / Lv 1. */
export function topBarValues(profile: ProfileLike | null, wallet: WalletLike | null): TopBarValues {
  const money = wallet ?? profile
  const into = count(profile?.xpIntoLevel)
  const next = count(profile?.xpForNext)
  return {
    coins: count(money?.coins),
    gems: count(money?.gems),
    level: Math.max(1, count(profile?.level)),
    xp: next > 0 ? Math.min(1, into / next) : 0,
  }
}

/** Guests see "Sign in to unlock"; only a profile that says `isGuest: false` is signed in (O-27). */
export function isGuest(profile: ProfileLike | null): boolean {
  return profile?.isGuest !== false
}
